/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, ember/no-observers, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
import { action, computed } from '@ember/object';
import { not, filterBy, alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import NotificationType from 'linced-app/enums/notification-type';
import {
  chequeNoValidations,
  chequeBSBValidations,
  chequeAccountNoValidations,
  moneyOrderNumberValidations,
  moneyOrderTextValidations,
} from 'linced-app/utils/finance';
import AuTaxType from '@linc-technologies/linced-common/enums/au-tax-type';
import Options from '@linc-technologies/linced-common/enums/options';
import PaymentMethod from '@linc-technologies/linced-common/enums/payment-method';
import { optionAlias } from '@linc-technologies/linced-common/utils/options';
import ENV from 'linced-app/config/environment';
import moment from 'moment';
import { observes } from '@ember-decorators/object';

export default class IndexController extends Controller {
  // Services
  @service
  features;

  @service
  fetch;

  @service
  finance;

  @service
  options;

  @service
  paperToaster;

  @service
  personOptions;

  @service
  print;

  @service
  session;

  @service
  store;

  @service
  strings;

  @service
  tax;

  @tracked
  hideArchivedLineItems = false;

  @alias('session.school.name')
  schoolName;

  @optionAlias('finance:settings:caregiverAccessEnabled')
  caregiverAccessEnabled;

  @optionAlias(Options.NOTIFICATIONS_EMAIL_ENABLED)
  emailNotificationsEnabled;

  @not('emailNotificationsEnabled')
  emailNotificationsDisabled;

  PaymentMethod = PaymentMethod;

  @alias('finance.availablePaymentMethods')
  paymentMethods;

  chequeNoValidations = chequeNoValidations;

  chequeBSBValidations = chequeBSBValidations;

  chequeAccountNoValidations = chequeAccountNoValidations;

  moneyOrderNumberValidations = moneyOrderNumberValidations;

  moneyOrderTextValidations = moneyOrderTextValidations;

  get tillDisabled() {
    return this.finance.tillDisabled(this.paymentMethod);
  }

  @computed('model.{accounts.[],person.id}')
  get learnerAccount() {
    return this.model.accounts.find(
      (item) => item.person_id === this.model.person.id,
    );
  }

  @tracked
  addPIADialogOpen = false;

  @tracked
  transferPIADialogOpen = false;

  @tracked
  transferPIAToSiblingDialogOpen = false;

  // transferPIAMaxAmount calculates the maximum amount which can be transferred from PIA.
  @computed('learnerAccount.balance', 'paymentItem', 'paymentItem.balance')
  get transferPIAMaxAmount() {
    let accountBalance = this.get('learnerAccount.balance');
    if (isEmpty(this.paymentItem)) {
      return accountBalance;
    }
    return Math.min(accountBalance, this.get('paymentItem.balance'));
  }

  @computed('model.{accounts.[],person.id}')
  get siblingAccounts() {
    return this.model.accounts.filter(
      (item) => item.person_id !== this.model.person.id,
    );
  }

  @computed(
    'model.{ledgerItems.[],futureLedgerItems.[]}',
    'features.sapFinance',
  )
  get ledgerItems() {
    let ledgerItems = this.model.ledgerItems;
    if (!isEmpty(this.model.futureLedgerItems)) {
      ledgerItems = [
        ...this.model.ledgerItems.slice(),
        ...this.model.futureLedgerItems.slice(),
      ];
    }
    if (this.features.sapFinance) {
      // Filter to items that have been sent to SAP.
      ledgerItems = ledgerItems.filter((item) => item.nswSapOk);
    }
    return ledgerItems.filter((item) => !item.event_id);
  }

  @computed('model.ledgers.[]', 'features.sapFinance')
  get availableLedgers() {
    if (!this.features.sapFinance) {
      // For most schools, simply filter out hidden Ledgers.
      return this.model.ledgers.filter((item) => !item.isHidden);
    }

    // For SAP schools, filter out hidden and inactive Ledgers.
    return this.model.ledgers.filter(
      (ledger) => ledger.isActive && !ledger.isHidden,
    );
  }

  @filterBy('model.costCentres', 'isActive')
  availableCostCentres;

  @filterBy('model.internalOrders', 'isActive')
  availableInternalOrders;

  // NSW DoE prohibited financial language (SMOS-012).
  // Populated by Finance::ProhibitedLanguageAlert via @onChange.
  @tracked
  itemNameViolations = [];

  @action
  setItemNameViolations(value) {
    this.itemNameViolations = value;
  }

  @tracked
  itemDescriptionViolations = [];

  @action
  setItemDescriptionViolations(value) {
    this.itemDescriptionViolations = value;
  }

  @computed('itemNameViolations.[]', 'itemDescriptionViolations.[]')
  get hasLanguageViolations() {
    return (
      !isEmpty(this.itemNameViolations) ||
      !isEmpty(this.itemDescriptionViolations)
    );
  }

  @tracked
  addItemDialogOpen = false;

  addItemMode = 'ledger';

  @tracked
  itemLedgerItem = null;

  @action
  setItemLedgerItem(value) {
    this.itemLedgerItem = value;
  }

  @tracked
  itemLedger = null;

  @tracked
  itemCostCentre = null;

  @action
  setItemCostCentre(value) {
    this.itemCostCentre = value;
  }

  @tracked
  itemInternalOrder = null;

  @action
  setItemInternalOrder(value) {
    this.itemInternalOrder = value;
  }

  @tracked
  itemAmount = null;

  @action
  setItemAmount(value) {
    this.itemAmount = value;
  }

  @tracked
  itemDescription = '';

  @action
  setItemDescription(value) {
    this.itemDescription = value;
  }

  @tracked
  itemName = '';

  @action
  setItemName(value) {
    this.itemName = value;
  }

  @tracked
  itemIsTaxable = true;

  @action
  setItemIsTaxable(value) {
    this.itemIsTaxable = value;
  }

  @tracked
  itemIsDonation = false;

  @action
  setItemIsDonation(value) {
    this.itemIsDonation = value;
  }

  @tracked
  itemIsRefundable = true;

  @action
  setItemIsRefundable(value) {
    this.itemIsRefundable = value;
  }

  @observes('itemLedgerItem', 'addItemMode')
  _resetAddItemFields() {
    if (this.addItemMode === 'new') {
      this.itemDate = moment().format('YYYY-MM-DD');
      this.itemDescription = '';
      this.itemName = '';
      this.itemAmount = null;
      this.itemLedger = null;
      this.itemCostCentre = null;
      this.itemInternalOrder = null;
      this.itemIsTaxable = true;
      this.itemIsDonation = false;
      this.itemIsRefundable = true;
    } else if (this.itemLedgerItem) {
      this.itemDate = this.itemLedgerItem.defaultDate;
      this.itemDescription = this.itemLedgerItem.defaultDescription;
      this.itemName = this.itemLedgerItem.defaultName;
      this.itemAmount = this.itemLedgerItem.defaultAmount;
      this.itemIsTaxable = this.itemLedgerItem.isTaxable;
      this.itemIsDonation = this.itemLedgerItem.isDonation;
      this.itemCostCentre = this.itemLedgerItem.costCentre;
      this.itemInternalOrder = this.itemLedgerItem.internalOrder;
      this.itemIsRefundable = this.itemLedgerItem.isRefundable;
    }
  }

  @tracked
  filter = 'unpaid';

  @action
  setFilter(value) {
    this.filter = value;
  }

  @tracked
  currentYear = moment().format('YYYY');

  @computed('filter', 'model.items.[]', 'currentYear', 'hideArchivedLineItems')
  get filteredItems() {
    let items = this.model.items;
    if (this.hideArchivedLineItems) {
      items = items.filter((item) => !item.isArchived);
    }
    switch (this.filter) {
      case 'current':
        return items.filter((item) => {
          return moment(item.date).format('YYYY') === this.currentYear;
        });
      case 'unpaid':
        return items.filter((item) => {
          return item.balance > 0;
        });
    }
    return items;
  }

  @tracked
  piaAccount = null;

  @tracked
  paymentAmount = null;

  @action
  setPaymentAmount(value) {
    this.paymentAmount = value;
  }

  @tracked
  paymentMethod = null;

  @tracked
  paymentComment = '';

  @action
  setPaymentComment(value) {
    this.paymentComment = value;
  }

  @tracked
  paymentPerson = null;

  @tracked
  paymentPersonName = '';

  @action
  setPaymentPersonName(value) {
    this.paymentPersonName = value;
  }

  @tracked
  paymentDate = moment().format('YYYY-MM-DD');

  @action
  setPaymentDate(value) {
    this.paymentDate = value;
  }

  @tracked
  paymentChequeNo = '';

  @action
  setPaymentChequeNo(value) {
    this.paymentChequeNo = value;
  }

  @tracked
  paymentChequeBSB = '';

  @action
  setPaymentChequeBSB(value) {
    this.paymentChequeBSB = value;
  }

  @tracked
  paymentChequeAccountNo = '';

  @action
  setPaymentChequeAccountNo(value) {
    this.paymentChequeAccountNo = value;
  }

  @tracked
  paymentMoneyOrderNumber = '';

  @action
  setPaymentMoneyOrderNumber(value) {
    this.paymentMoneyOrderNumber = value;
  }

  @tracked
  paymentMoneyOrderIssuer = '';

  @action
  setPaymentMoneyOrderIssuer(value) {
    this.paymentMoneyOrderIssuer = value;
  }

  @tracked
  paymentMoneyOrderPayee = '';

  @action
  setPaymentMoneyOrderPayee(value) {
    this.paymentMoneyOrderPayee = value;
  }

  @tracked
  statementDialogOpen = false;

  @tracked
  statementInProgress = false;

  @tracked
  statementComplete = false;

  @tracked
  statementDistributionMethod = null;

  @action
  setStatementDistributionMethod(value) {
    this.statementDistributionMethod = value;
  }

  @tracked
  statementEmailSubject = '';

  @action
  setStatementEmailSubject(value) {
    this.statementEmailSubject = value;
  }

  @tracked
  statementEmailMessage = '';

  @action
  setStatementEmailMessage(value) {
    this.statementEmailMessage = value;
  }

  @tracked
  statementExcludePaidItems = false;

  @action
  setStatementExcludePaidItems(value) {
    this.statementExcludePaidItems = value;
  }

  @tracked
  statementSiblings = [];

  @tracked
  statementEmailRecipients = [];

  @tracked
  addPIAInProgress;

  @tracked
  errorMessage;

  @tracked
  itemAddInProgress;

  @tracked
  statementGenerating;

  @tracked
  transferPIAInProgress;

  @tracked
  transferPIAToSiblingInProgress;

  @tracked
  itemDate;

  @action
  setItemDate(value) {
    this.itemDate = value;
  }

  @tracked
  addPaymentDialogOpen;

  @tracked
  discountDialogOpen;

  @tracked
  paymentItem;

  @action
  setPaymentItem(value) {
    this.paymentItem = value;
  }

  @tracked
  transferAccount;

  @action
  setTransferAccount(value) {
    this.transferAccount = value;
  }

  @tracked
  voidDialogOpen;

  @tracked
  writeoffDialogOpen;

  @computed('model.accounts.@each.caregiver_ids')
  get availableEmailRecipients() {
    let recipients = [];
    this.model.accounts.forEach((account) => {
      account.caregivers.forEach((caregiver) => {
        if (!recipients.find((item) => item.id === caregiver.id)) {
          recipients.push(caregiver);
        }
      });
    });
    return recipients
      .slice()
      .sort((a, b) => (a.fullName || '').localeCompare(b.fullName || ''));
  }

  @computed('statementDistributionMethod', 'statementEmailRecipients.[]')
  get statementFormIsInvalid() {
    switch (this.statementDistributionMethod) {
      case 'email':
        // At least one recipient is required to send emails.
        return isEmpty(this.statementEmailRecipients);

      case 'print':
        // Nothing is required here.
        return false;

      default:
        // No method chosen - can't generate.
        return true;
    }
  }

  sendStatementEmails() {
    let recipients = this.statementEmailRecipients.map((caregiver) => {
      let salutation = caregiver.get('fullName');
      if (isEmpty(salutation)) {
        // If we don't have a salutation yet, use the account salutation.
        salutation = this.print.getAccountSalutation(
          this.learnerAccount,
          this.model.accounts,
        );
      }
      return {
        personId: caregiver.id,
        salutation,
      };
    });

    let notification = this.store.createRecord('notifications/notification', {
      schoolId: this.session.school,
      notificationType: NotificationType.BILLING_STATEMENT,
      params: {
        subject: this.statementEmailSubject,
        message: this.statementEmailMessage,
        recipients,
      },
    });

    notification
      .save()
      .then(() => {
        this.statementInProgress = false;
        this.statementComplete = true;
      })
      .catch(() => {
        this.errorMessage =
          'Something went wrong.  Please try again later, or contact Hero support if the problem persists.';
        this.statementInProgress = false;
      });
  }

  generatePrintStatement() {
    let selectedAccountIds = this.statementSiblings.map((item) => item.id);
    let thisYear = moment().year();
    let filteredItems = this.model.items.filter((item) => {
      let itemYear = moment(item.date).year();
      if (
        !item.balance &&
        (this.statementExcludePaidItems || itemYear < thisYear)
      ) {
        // Skip paid items (if in a previous year, or if option to skip is selected).
        return false;
      }
      let accountId = item.get('account.id');
      return (
        accountId === this.learnerAccount.id ||
        selectedAccountIds.includes(accountId)
      );
    });
    let filteredAccounts = this.model.accounts.filter((account) => {
      return (
        account.id === this.learnerAccount.id ||
        selectedAccountIds.includes(account.id)
      );
    });

    let options = {
      includeHomeroomName: true,
      includeLearnerName: true,
    };
    this.print.statement(
      filteredItems,
      this.model.person,
      filteredAccounts,
      (success) => {
        if (success) {
          this.statementInProgress = false;
          this.statementComplete = true;
          this.paperToaster.show(`Statement downloaded successfully.`, {
            duration: 4000,
            position: 'top right',
            toastClass: 'success',
          });
        } else {
          this.statementInProgress = false;
          this.statementComplete = false;
          this.errorMessage =
            'Something went wrong.  Please try again later, or contact Hero support if the problem persists.';
          this.paperToaster.show('Error generating statement!', {
            duration: 4000,
            position: 'top right',
            toastClass: 'warning',
          });
        }
      },
      options,
    );
  }

  @action
  toggleHideArchivedLineItems(value) {
    this.hideArchivedLineItems = !value;
    this.personOptions.setHideArchivedLineItems(!value);
  }

  @action
  toggleAddPIADialog(account) {
    this.errorMessage = '';
    this.piaAccount = account;
    this.toggleProperty('addPIADialogOpen');
    if (!this.addPIADialogOpen) {
      this.resetPaymentForm();
    }
  }

  @action
  setPaidBy(billPayer) {
    this.paymentPerson = billPayer;
    this.paymentPersonName = billPayer.fullName;
  }

  @action
  setPaidByName(name) {
    this.paymentPerson = null;
    this.paymentPersonName = name;
  }

  @action
  addPIA() {
    this.addPIAInProgress = true;
    let url = `${ENV.APP.apiHost}/finance/${ENV.APP.apiPath}/accounts/${this.piaAccount.id}/:pay`;
    let tillId;
    if (!this.tillDisabled && !isEmpty(this.get('finance.activeTill'))) {
      tillId = parseInt(this.get('finance.activeTill.id'), 10);
    }
    if (this.paymentMethod !== PaymentMethod.CHEQUE) {
      // Clear cheque detail fields if the payment isn't by cheque.
      this.paymentChequeNo = '';
      this.paymentChequeBSB = '';
      this.paymentChequeAccountNo = '';
    }
    if (this.paymentMethod !== PaymentMethod.MONEY_ORDER) {
      // Clear MO detail fields if the payment isn't by Money Order.
      this.paymentMoneyOrderNumber = '';
      this.paymentMoneyOrderIssuer = '';
      this.paymentMoneyOrderPayee = '';
    }
    let data = {
      accountId: parseInt(this.piaAccount.id, 10),
      method: this.paymentMethod,
      comment: this.paymentComment,
      date: this.paymentDate,
      amount: Math.round(this.paymentAmount * 100),
      chequeNo: this.paymentChequeNo,
      chequeBSB: this.paymentChequeBSB,
      chequeAccountNo: this.paymentChequeAccountNo,
      moneyOrderNumber: this.paymentMoneyOrderNumber,
      moneyOrderIssuer: this.paymentMoneyOrderIssuer,
      moneyOrderPayee: this.paymentMoneyOrderPayee,
      tillId,
    };
    // Only personId OR personName can be set - not both.
    if (!isEmpty(this.paymentPerson)) {
      data.personId = this.get('paymentPerson.caregiverId');
    } else {
      data.personName = this.paymentPersonName;
    }
    let options = {
      data: JSON.stringify(data),
    };
    this.fetch
      .post(url, options)
      .then(() => {
        this.paperToaster.show(`Payment in Advance created successfully.`, {
          duration: 4000,
          position: 'top right',
          toastClass: 'success',
        });
        this.piaAccount.reload();
        this.addPIADialogOpen = false;
        this.resetPaymentForm();
      })
      .catch(() => {
        this.paperToaster.show('Error creating Payment in Advance!', {
          duration: 4000,
          position: 'top right',
          toastClass: 'warning',
        });
        if (!isEmpty(tillId)) {
          // Update till sessions, in case somebody else started one for this
          // till after the payment dialog was opened.
          this.finance.updateTillSessions();
        }
      })
      .then(() => {
        this.addPIAInProgress = false;
      });
  }

  @action
  setPaymentMethod(method) {
    this.paymentMethod = method;
  }

  @action
  toggleTransferPIADialog() {
    this.errorMessage = '';
    this.toggleProperty('transferPIADialogOpen');
    if (!this.transferPIADialogOpen) {
      this.resetPaymentForm();
    }
  }

  @action
  transferPIA() {
    this.transferPIAInProgress = true;
    let url = `${ENV.APP.apiHost}/finance/${ENV.APP.apiPath}/accounts/${this.learnerAccount.id}/:transfer`;
    let data = {
      accountId: parseInt(this.learnerAccount.id, 10),
      date: this.paymentDate,
      transfers: [
        {
          lineItemId: parseInt(this.paymentItem.id, 10),
          amount: Math.round(this.paymentAmount * 100),
          comment: this.paymentComment,
        },
      ],
    };
    let options = {
      data: JSON.stringify(data),
    };
    this.fetch
      .post(url, options)
      .then(() => {
        this.paperToaster.show(`Payment in Advance transferred successfully.`, {
          duration: 4000,
          position: 'top right',
          toastClass: 'success',
        });
        this.learnerAccount.reload();
        this.paymentItem.reload();
        // Explicitly reload all transactions for this line item
        // So we keep our Receipt and Other Credit values up-to-date
        this.store.query('finance/transaction', {
          lineItemId: this.paymentItem.id,
        });
        this.transferPIADialogOpen = false;
        this.resetPaymentForm();
      })
      .catch(() => {
        this.paperToaster.show('Error transferring Payment in Advance!', {
          duration: 4000,
          position: 'top right',
          toastClass: 'warning',
        });
      })
      .then(() => {
        this.transferPIAInProgress = false;
      });
  }

  @action
  toggleTransferPIAToSiblingDialog() {
    this.errorMessage = '';
    this.toggleProperty('transferPIAToSiblingDialogOpen');
    if (!this.transferPIAToSiblingDialogOpen) {
      this.resetPaymentForm();
    }
  }

  @action
  transferPIAToSibling() {
    this.transferPIAToSiblingInProgress = true;
    let url = `${ENV.APP.apiHost}/finance/${ENV.APP.apiPath}/accounts/${this.learnerAccount.id}/:transfer`;
    let data = {
      accountId: parseInt(this.learnerAccount.id, 10),
      date: this.paymentDate,
      transfers: [
        {
          accountId: parseInt(this.transferAccount.id, 10),
          amount: Math.round(this.paymentAmount * 100),
          comment: this.paymentComment,
        },
      ],
    };
    let options = {
      data: JSON.stringify(data),
    };
    this.fetch
      .post(url, options)
      .then(() => {
        this.paperToaster.show(`Payment in Advance transferred successfully.`, {
          duration: 4000,
          position: 'top right',
          toastClass: 'success',
        });
        this.learnerAccount.reload();
        this.transferAccount.reload();
        this.transferPIAToSiblingDialogOpen = false;
        this.resetPaymentForm();
      })
      .catch(() => {
        this.paperToaster.show('Error transferring Payment in Advance!', {
          duration: 4000,
          position: 'top right',
          toastClass: 'warning',
        });
      })
      .then(() => {
        this.transferPIAToSiblingInProgress = false;
      });
  }

  @action
  toggleAddItemDialog() {
    this.errorMessage = '';
    this.toggleProperty('addItemDialogOpen');
    if (!this.addItemDialogOpen) {
      this.resetAddItemForm();
    }
  }

  @action
  setAddItemMode(mode) {
    this.set('addItemMode', mode);
  }

  @action
  selectLedger(ledger) {
    this.itemLedger = ledger;
    this.itemInternalOrder = null;
  }

  @action
  async addItem() {
    let itemData = {
      accountId: this.learnerAccount,
      description: this.itemDescription,
      date: this.itemDate,
      amount: this.itemAmount,
      isTaxable: this.itemIsTaxable,
      isDonation: this.itemIsDonation,
      isRefundable: this.itemIsRefundable,
    };
    switch (this.addItemMode) {
      case 'ledger':
        itemData.ledgerItemId = this.itemLedgerItem;
        // A belongsTo takes a record, not the async proxy.
        itemData.ledgerId = await this.itemLedgerItem.ledger;
        itemData.costCentreId = await this.itemLedgerItem.costCentreId;
        itemData.internalOrderId = await this.itemLedgerItem.internalOrderId;
        itemData.name = this.itemLedgerItem.name;
        break;
      case 'new':
        itemData.ledgerId = this.itemLedger;
        itemData.costCentreId = this.itemCostCentre;
        itemData.internalOrderId = this.itemInternalOrder;
        itemData.name = this.itemName;
        break;
    }

    let taxType = itemData.ledgerId.get('taxType');
    if (this.features.sapFinance) {
      itemData.taxType = taxType;
      itemData.isTaxable = taxType === AuTaxType.S1;
      itemData.isDonation = itemData.ledgerId.get('isDeductibleGiftReceipt');
    }

    if (itemData.isTaxable) {
      itemData.taxPercentage = this.tax.getRate(itemData.date, taxType) || 0;
    }

    this.store
      .createRecord('finance/line-item', itemData)
      .save()
      .then(() => {
        this.paperToaster.show(`Line Item created successfully.`, {
          duration: 4000,
          position: 'top right',
          toastClass: 'success',
        });
        this.addItemDialogOpen = false;
        this.resetAddItemForm();
        this.model.items.update();
      })
      .catch(() => {
        this.paperToaster.show('Error creating Line Item!', {
          duration: 4000,
          position: 'top right',
          toastClass: 'warning',
        });
      })
      .then(() => {
        this.itemAddInProgress = false;
      });
  }

  @action
  statement() {
    this.statementGenerating = true;
    let options = {
      includeHomeroomName: true,
      includeLearnerName: true,
    };
    this.print.statement(
      this.filteredItems,
      this.model.person,
      this.model.accounts,
      (success) => {
        this.statementGenerating = false;
        if (success) {
          this.paperToaster.show(`Statement downloaded successfully.`, {
            duration: 4000,
            position: 'top right',
            toastClass: 'success',
          });
        } else {
          this.paperToaster.show('Error generating statement!', {
            duration: 4000,
            position: 'top right',
            toastClass: 'warning',
          });
        }
      },
      options,
    );
  }

  @action
  openStatementDialog() {
    this.statementDialogOpen = true;
  }

  @action
  closeStatementDialog() {
    this.resetStatementForm();
    this.statementDialogOpen = false;
  }

  @action
  toggleStatementSibling(account, checked) {
    if (checked) {
      this.statementSiblings = [...this.statementSiblings, account];
    } else {
      this.statementSiblings = this.statementSiblings.filter(
        (item) => item !== account,
      );
    }
  }

  @action
  toggleStatementEmailRecipient(caregiver, checked) {
    if (checked) {
      this.statementEmailRecipients = [
        ...this.statementEmailRecipients,
        caregiver,
      ];
    } else {
      this.statementEmailRecipients = this.statementEmailRecipients.filter(
        (item) => item !== caregiver,
      );
    }
  }

  @action
  generateStatement() {
    this.statementInProgress = true;
    this.errorMessage = '';

    switch (this.statementDistributionMethod) {
      case 'email':
        this.sendStatementEmails();
        break;
      case 'print':
        this.generatePrintStatement();
        break;
    }
  }

  resetPaymentForm() {
    this.paymentAmount = null;
    this.paymentMethod = null;
    this.paymentComment = '';
    this.paymentPerson = null;
    this.paymentPersonName = '';
    this.transferAccount = null;
    this.paymentItem = null;
    this.paymentDate = moment().format('YYYY-MM-DD');
    this.paymentChequeNo = '';
    this.paymentChequeBSB = '';
    this.paymentChequeAccountNo = '';
    this.paymentMoneyOrderNumber = '';
    this.paymentMoneyOrderIssuer = '';
    this.paymentMoneyOrderPayee = '';
  }

  resetAddItemForm() {
    this.setProperties({
      itemLedgerItem: null,
      itemLedger: null,
      itemAmount: null,
      itemIsTaxable: true,
      itemIsDonation: false,
      itemIsRefundable: true,
      addItemMode: 'ledger',
    });
  }

  resetStatementForm() {
    this.statementInProgress = false;
    this.statementComplete = false;
    this.statementDistributionMethod = null;
    this.statementEmailSubject = `Financial Statement from ${this.schoolName}`;
    this.statementEmailMessage =
      'Recent updates have been applied to your student billing account.  Please click the link below to log in to your school account to review your statement.';
    this.statementExcludePaidItems = false;
    this.statementSiblings = this.siblingAccounts.slice();
    this.errorMessage = '';

    // Once the selected siblings list has been updated, update the selected email recipients list via computed property
    this.statementEmailRecipients = this.availableEmailRecipients.filter(
      (item) => item.fields['core:emailAddress']?.value,
    );
  }

  reset() {
    this.resetPaymentForm();
    this.resetAddItemForm();
    this.resetStatementForm();
    this.addPIADialogOpen = false;
    this.transferPIADialogOpen = false;
    this.transferPIAToSiblingDialogOpen = false;
    this.addPaymentDialogOpen = false;
    this.discountDialogOpen = false;
    this.writeoffDialogOpen = false;
    this.voidDialogOpen = false;
    this.filter = 'unpaid';
    this.currentYear = moment().format('YYYY');
    this.statementDialogOpen = false;
  }
}
